import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { theme as appTheme } from '@/theme';

import { useConfigurations } from '@/features/_data/useConfigurations';
import { useCreateUnit } from '@/features/_data/useUnitData';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { Button } from '@/ui/Button/Button';
import { Input } from '@/ui/Input/Input';
import { Modal } from '@/ui/Modal/Modal';
import Select, {
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  GroupBase,
} from 'react-select';
import { useTemplates } from '@/features/_data/useTemplates';
import { Template } from '@/types/Template';

import { Flex } from '@/ui/Flex/Flex';
import { UserInfo, useUserInfo } from '@/features/_data/useUserInfo';
import { ProductType } from '@/types/ProductType';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';

type Props = {
  readonly isModalOpen: boolean;
  readonly setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const StyledSaveButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  margin-top: 25px;
  align-self: flex-end;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

const selectStyles = {
  dropdownIndicator: (
    base: CSSObjectWithLabel,
    state: DropdownIndicatorProps<
      {
        label: string;
        value: string;
      },
      false,
      GroupBase<{
        label: string;
        value: string;
      }>
    >,
  ) => ({
    ...base,
    color: appTheme.color('#1B1A1F'),
    ':hover': {
      color: appTheme.color('#1B1A1F'),
    },
    rotate: state.selectProps.menuIsOpen ? '180deg' : undefined,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base: CSSObjectWithLabel) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
  }),
};

export const CreateUnitModal = ({ isModalOpen, setIsModalOpen }: Props) => {
  const toast = useToast();
  const [selectedCustomerId] = useSelectedCustomerId();
  const [isCreating, setIsCreating] = useState(false);
  const { createUnit } = useCreateUnit();
  const { refetch: refetchConfigurations } =
    useConfigurations(selectedCustomerId);
  const { data: userInfo, isAdmin } = useUserInfo();

  const { data: templates, isLoading: isTemplatesLoading } = useTemplates();

  const [unitName, setUnitName] = useState('');
  const [selectedUnitTemplate, setSelectedUnitTemplate] = useState<
    Template | undefined
  >();
  const [selectedScriptTemplate, setSelectedScriptTemplate] = useState<
    Template | undefined
  >();
  const [selectedProductType, setSelectedProductType] = useState<ProductType>(
    ProductType.Generic,
  );

  const unitTemplatesOptions = [
    { label: 'None', value: '' },
    ...(templates
      ?.filter((template: Template) => template.type === 'UNIT')
      ?.map((template: Template) => ({
        label: template.name,
        value: template.id,
      })) || []),
  ];

  const scriptTemplatesOptions = [
    { label: 'None', value: '' },
    ...(templates
      ?.filter((template: Template) => template.type === 'SCRIPT')
      ?.map((template: Template) => ({
        label: template.name,
        value: template.id,
      })) || []),
  ];

  const handleSave = async () => {
    if (!selectedCustomerId) {
      if (isAdmin || !unitName)
        return toast({
          mode: TOASTTYPE.Danger,
          title: 'No customer selected.',
          subtitle: 'Please select a customer or try to restart the app.',
        });
    }
    setIsCreating(true);
    await createUnit({
      name: unitName,
      customer: isAdmin
        ? (selectedCustomerId as string)
        : (userInfo as UserInfo).customer,
      complexityType: 'complex',
      shelves: [],
      subsidiaries: [],
      scriptTemplateId: selectedScriptTemplate?.id,
      unitTemplateId: selectedUnitTemplate?.id,
      productType: selectedProductType,
      logicEditorEnabled: true,
    });
    setIsCreating(false);
    setIsModalOpen(false);
    await refetchConfigurations();
  };

  return isModalOpen ? (
    <Modal onClose={() => setIsModalOpen(false)} title="Add Configuration">
      <Flex gap={8} direction="column">
        <Input
          onChange={(value) => setUnitName(value)}
          placeholder="Name"
          value={unitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
            }
          }}
          maxLength={30}
        />
        <Select
          options={unitTemplatesOptions}
          placeholder="Content Template"
          isDisabled={isTemplatesLoading}
          value={
            selectedUnitTemplate
              ? {
                  label: selectedUnitTemplate.name,
                  value: selectedUnitTemplate.id,
                }
              : null
          }
          onChange={(selectedOption) => {
            setSelectedUnitTemplate(
              selectedOption?.value
                ? templates?.find(
                    (template: Template) =>
                      template.id === selectedOption.value,
                  )
                : undefined,
            );
          }}
          styles={selectStyles}
        />
        <Select
          options={scriptTemplatesOptions}
          placeholder="Logic Template"
          isDisabled={isTemplatesLoading}
          value={
            selectedScriptTemplate
              ? {
                  label: selectedScriptTemplate.name,
                  value: selectedScriptTemplate.id,
                }
              : null
          }
          onChange={(selectedOption) => {
            setSelectedScriptTemplate(
              selectedOption?.value
                ? templates?.find(
                    (template: Template) =>
                      template.id === selectedOption.value,
                  )
                : undefined,
            );
          }}
          styles={selectStyles}
        />
        <Select
          options={[
            { label: ProductType.Generic, value: ProductType.Generic },
            { label: ProductType.TrihowPad, value: ProductType.TrihowPad },
          ]}
          placeholder="Product Type"
          value={{ label: selectedProductType, value: selectedProductType }}
          onChange={(selectedOption) => {
            if (selectedOption) {
              setSelectedProductType(selectedOption.value);
            }
          }}
        />
      </Flex>

      <StyledSaveButton
        disabled={!unitName}
        onClick={handleSave}
        loading={isCreating}
      >
        <FormattedMessage defaultMessage="Save" id="jvo0vs" />
      </StyledSaveButton>
    </Modal>
  ) : null;
};
